<template>
  <div class="sign-in-shell bg-background relative isolate flex min-h-dvh flex-col overflow-x-hidden">
    <div
      class="pointer-events-none absolute inset-y-0 right-0 z-0 hidden w-[var(--hero-width)] lg:block"
      aria-hidden="true">
      <div class="absolute inset-0 overflow-hidden">
        <img :src="resolvedHero" alt="" class="absolute inset-0 size-full object-cover" />
        <div v-if="!skipHeroOverlay" class="absolute inset-0 bg-black/25"></div>
      </div>
      <img :src="heroEdgeMask" alt="" class="absolute top-0 left-0 h-full w-[146px]" />
    </div>

    <!-- Keep a real box (not display:contents) so LanguageMenu positioning/clicks work. -->
    <header
      class="relative z-20 flex items-center justify-end px-3 pt-3 lg:pointer-events-none lg:absolute lg:inset-x-0 lg:top-0 lg:z-20 lg:block lg:p-0">
      <div class="flex items-center gap-3 lg:pointer-events-auto lg:absolute lg:top-[31px] lg:right-[42px]">
        <button
          type="button"
          class="bg-surface text-foreground rounded-[4px] px-4 py-3.5 text-sm font-medium"
          @click="openContactForm = true">
          {{ $t("Support") }}
        </button>

        <LanguageMenu>
          <template #trigger="{ locale, language, open }">
            <button
              type="button"
              class="bg-surface text-foreground flex items-center gap-1.5 rounded-[4px] px-4 py-3.5 text-sm font-medium"
              aria-haspopup="menu"
              :aria-expanded="open"
              :aria-label="`${$t('Language')}: ${$t(language?.name ?? locale)}`">
              <LanguageIcon class="size-4 shrink-0" aria-hidden="true" />
              <span>{{ $t(language?.name ?? locale) }}</span>
              <ChevronDownIcon
                class="size-3 shrink-0 transition-transform"
                :class="open && 'rotate-180'"
                aria-hidden="true" />
            </button>
          </template>
        </LanguageMenu>
      </div>
    </header>

    <ContactForm :open="openContactForm" @hide-modal="openContactForm = false" />

    <div class="relative z-10 flex justify-center px-4 pt-10 pb-6 lg:hidden" aria-label="XS2Event">
      <div class="flex items-center gap-1.5">
        <svg class="size-8 shrink-0 overflow-hidden rounded-sm" viewBox="0 0 567 567" aria-hidden="true">
          <rect fill="currentColor" class="text-primary" width="567" height="567" />
          <path
            fill="currentColor"
            class="text-white"
            d="m420.41,283.43l.08-.08-70.08-70.08h0l-105.56-105.55c-5.18-5.2-12.67-8.31-20.28-8.19-7.7,0-15.09,2.98-20.3,8.18l-29.42,29.43c-11.18,11.18-11.18,29.39,0,40.58l105.57,105.55h0l.08.08-105.65,105.65c-5.42,5.42-8.4,12.62-8.4,20.29s2.98,14.88,8.4,20.3l29.43,29.43c5.42,5.41,12.62,8.39,20.28,8.39s14.87-2.98,20.29-8.39l105.57-105.58h0l70-70h0Z" />
        </svg>
        <svg class="h-6.5 shrink-0 text-foreground" viewBox="0 0 126 17" fill="currentColor" aria-hidden="true">
          <path
            d="M27.6091 7.40698C26.816 7.12596 25.8431 6.87743 24.6899 6.66113C23.7958 6.48805 23.1545 6.32574 22.7653 6.17448C22.376 6.02321 22.1814 5.79614 22.1814 5.49334C22.1814 4.85932 22.8371 4.54178 24.1492 4.54178C25.4753 4.54178 26.8809 4.91687 28.3659 5.66633L29.9013 1.96871C29.137 1.53615 28.2542 1.20845 27.2522 0.984662C26.2502 0.761226 25.2304 0.649626 24.1925 0.649626C22.6788 0.649626 21.3922 0.873063 20.3326 1.3197C19.2729 1.76705 18.4763 2.37961 17.943 3.15808C17.4097 3.93644 17.143 4.80139 17.143 5.75291C17.143 6.8485 17.4312 7.71737 18.008 8.35846C18.5845 9.00031 19.2621 9.46125 20.0406 9.74264C20.8191 10.0237 21.7921 10.2869 22.96 10.5318C23.868 10.7191 24.5202 10.8922 24.9169 11.0507C25.3131 11.2094 25.5116 11.4475 25.5116 11.7643C25.5116 12.0382 25.3601 12.2438 25.0575 12.3808C24.7547 12.5177 24.2499 12.5861 23.5438 12.5861C22.7221 12.5861 21.8642 12.4602 20.9704 12.2077C20.0763 11.9556 19.2693 11.6206 18.5486 11.2022L16.905 14.9213C17.669 15.3973 18.6459 15.7755 19.8352 16.0566C21.0244 16.338 22.2463 16.4785 23.5004 16.4785C25.0142 16.4785 26.3007 16.2515 27.3604 15.7973C28.4201 15.3432 29.2164 14.7308 29.7498 13.9591C30.283 13.1881 30.5499 12.3264 30.5499 11.375C30.5499 10.2797 30.258 9.41474 29.6742 8.78007C29.0903 8.14609 28.4017 7.6881 27.6091 7.40698Z" />
          <path
            d="M41.293 10.2076C42.3163 9.28511 43.0046 8.45978 43.358 7.73144C43.711 7.00376 43.8878 6.22143 43.8878 5.38543C43.8878 4.44853 43.6283 3.61964 43.1095 2.89855C42.5904 2.17807 41.8693 1.62267 40.9468 1.23331C40.0241 0.844308 38.9646 0.649748 37.7682 0.649748C36.1968 0.649748 34.856 0.963364 33.7461 1.59012C32.6359 2.21736 31.7925 3.07158 31.2162 4.1529L35.0653 6.07734C35.2814 5.63071 35.5912 5.2846 35.995 5.03931C36.3984 4.79435 36.8309 4.67174 37.2924 4.67174C37.7682 4.67174 38.1321 4.77645 38.3845 4.98526C38.6365 5.19445 38.7629 5.48629 38.7629 5.86099C38.7629 6.16387 38.6835 6.47384 38.525 6.79106C38.3663 7.10819 38.0492 7.49041 37.5735 7.93707L32.1027 12.997V16.1326H44.2338V12.1753H39.1306L41.293 10.2076Z" />
          <path
            d="M51.2279 10.2944H57.0989V6.61822H51.2279V4.84507H58.1656V0.995778H46.2109V16.1328H58.7961V12.2836H51.2279V10.2944Z" />
          <path
            d="M83.8098 10.2944H89.681V6.61822H83.8098V4.84507H90.7476V0.995778H78.793V16.1328H91.3782V12.2836H83.8098V10.2944Z" />
          <path
            d="M68.6741 9.83999L65.0627 0.995555H59.5703L65.9709 16.1326H70.988L77.3886 0.995555H72.3718L68.6741 9.83999Z" />
          <path
            d="M103.301 7.87198L97.5494 0.995555H93.3541V16.1326H98.3277V9.25612L104.08 16.1326H108.275V0.995555H103.301V7.87198Z" />
          <path
            d="M123.637 0.995555H110.251V4.95282H115.085V16.1326H120.188V4.95282H125.023V2.38079C125.023 1.61569 124.403 0.995555 123.637 0.995555Z" />
          <path
            d="M10.7151 8.50319L10.7186 8.49977L10.7145 8.4957L15.7568 3.45344L12.8035 0.5L7.76099 5.54247L3.30805 1.09007C3.08938 0.870777 2.77363 0.739651 2.45235 0.744385C2.12755 0.744385 1.81549 0.870067 1.596 1.08924L0.354575 2.33093C-0.117051 2.80277 -0.117051 3.57096 0.354575 4.04316L4.80775 8.49575L4.80443 8.49908L4.80845 8.50315L0.354575 12.9572C0.125927 13.1859 0 13.4898 0 13.8132C0 14.1366 0.125927 14.4408 0.354575 14.6695L1.59619 15.9111C1.82465 16.1393 2.12874 16.265 2.45194 16.265C2.77521 16.265 3.07922 16.1393 3.30786 15.9111L7.76113 11.4574L12.8035 16.5L12.8042 16.4993L12.8049 16.5L15.7585 13.5467L10.7151 8.50324L10.7151 8.50319Z" />
        </svg>
      </div>
    </div>

    <div class="relative z-10 flex min-h-0 flex-1 flex-col lg:flex-row">
      <section
        class="hidden lg:flex lg:w-[var(--copy-width)] lg:flex-col lg:justify-center lg:pr-10 lg:pl-[var(--copy-pad)]">
        <div class="flex items-center gap-3" aria-label="XS2Event">
          <svg class="size-11 shrink-0 overflow-hidden rounded-sm" viewBox="0 0 567 567" aria-hidden="true">
            <rect fill="currentColor" class="text-primary" width="567" height="567" />
            <path
              fill="currentColor"
              class="text-white"
              d="m420.41,283.43l.08-.08-70.08-70.08h0l-105.56-105.55c-5.18-5.2-12.67-8.31-20.28-8.19-7.7,0-15.09,2.98-20.3,8.18l-29.42,29.43c-11.18,11.18-11.18,29.39,0,40.58l105.57,105.55h0l.08.08-105.65,105.65c-5.42,5.42-8.4,12.62-8.4,20.29s2.98,14.88,8.4,20.3l29.43,29.43c5.42,5.41,12.62,8.39,20.28,8.39s14.87-2.98,20.29-8.39l105.57-105.58h0l70-70h0Z" />
          </svg>
          <svg class="h-11 text-foreground" viewBox="0 0 126 17" fill="currentColor" aria-hidden="true">
            <path
              d="M27.6091 7.40698C26.816 7.12596 25.8431 6.87743 24.6899 6.66113C23.7958 6.48805 23.1545 6.32574 22.7653 6.17448C22.376 6.02321 22.1814 5.79614 22.1814 5.49334C22.1814 4.85932 22.8371 4.54178 24.1492 4.54178C25.4753 4.54178 26.8809 4.91687 28.3659 5.66633L29.9013 1.96871C29.137 1.53615 28.2542 1.20845 27.2522 0.984662C26.2502 0.761226 25.2304 0.649626 24.1925 0.649626C22.6788 0.649626 21.3922 0.873063 20.3326 1.3197C19.2729 1.76705 18.4763 2.37961 17.943 3.15808C17.4097 3.93644 17.143 4.80139 17.143 5.75291C17.143 6.8485 17.4312 7.71737 18.008 8.35846C18.5845 9.00031 19.2621 9.46125 20.0406 9.74264C20.8191 10.0237 21.7921 10.2869 22.96 10.5318C23.868 10.7191 24.5202 10.8922 24.9169 11.0507C25.3131 11.2094 25.5116 11.4475 25.5116 11.7643C25.5116 12.0382 25.3601 12.2438 25.0575 12.3808C24.7547 12.5177 24.2499 12.5861 23.5438 12.5861C22.7221 12.5861 21.8642 12.4602 20.9704 12.2077C20.0763 11.9556 19.2693 11.6206 18.5486 11.2022L16.905 14.9213C17.669 15.3973 18.6459 15.7755 19.8352 16.0566C21.0244 16.338 22.2463 16.4785 23.5004 16.4785C25.0142 16.4785 26.3007 16.2515 27.3604 15.7973C28.4201 15.3432 29.2164 14.7308 29.7498 13.9591C30.283 13.1881 30.5499 12.3264 30.5499 11.375C30.5499 10.2797 30.258 9.41474 29.6742 8.78007C29.0903 8.14609 28.4017 7.6881 27.6091 7.40698Z" />
            <path
              d="M41.293 10.2076C42.3163 9.28511 43.0046 8.45978 43.358 7.73144C43.711 7.00376 43.8878 6.22143 43.8878 5.38543C43.8878 4.44853 43.6283 3.61964 43.1095 2.89855C42.5904 2.17807 41.8693 1.62267 40.9468 1.23331C40.0241 0.844308 38.9646 0.649748 37.7682 0.649748C36.1968 0.649748 34.856 0.963364 33.7461 1.59012C32.6359 2.21736 31.7925 3.07158 31.2162 4.1529L35.0653 6.07734C35.2814 5.63071 35.5912 5.2846 35.995 5.03931C36.3984 4.79435 36.8309 4.67174 37.2924 4.67174C37.7682 4.67174 38.1321 4.77645 38.3845 4.98526C38.6365 5.19445 38.7629 5.48629 38.7629 5.86099C38.7629 6.16387 38.6835 6.47384 38.525 6.79106C38.3663 7.10819 38.0492 7.49041 37.5735 7.93707L32.1027 12.997V16.1326H44.2338V12.1753H39.1306L41.293 10.2076Z" />
            <path
              d="M51.2279 10.2944H57.0989V6.61822H51.2279V4.84507H58.1656V0.995778H46.2109V16.1328H58.7961V12.2836H51.2279V10.2944Z" />
            <path
              d="M83.8098 10.2944H89.681V6.61822H83.8098V4.84507H90.7476V0.995778H78.793V16.1328H91.3782V12.2836H83.8098V10.2944Z" />
            <path
              d="M68.6741 9.83999L65.0627 0.995555H59.5703L65.9709 16.1326H70.988L77.3886 0.995555H72.3718L68.6741 9.83999Z" />
            <path
              d="M103.301 7.87198L97.5494 0.995555H93.3541V16.1326H98.3277V9.25612L104.08 16.1326H108.275V0.995555H103.301V7.87198Z" />
            <path
              d="M123.637 0.995555H110.251V4.95282H115.085V16.1326H120.188V4.95282H125.023V2.38079C125.023 1.61569 124.403 0.995555 123.637 0.995555Z" />
            <path
              d="M10.7151 8.50319L10.7186 8.49977L10.7145 8.4957L15.7568 3.45344L12.8035 0.5L7.76099 5.54247L3.30805 1.09007C3.08938 0.870777 2.77363 0.739651 2.45235 0.744385C2.12755 0.744385 1.81549 0.870067 1.596 1.08924L0.354575 2.33093C-0.117051 2.80277 -0.117051 3.57096 0.354575 4.04316L4.80775 8.49575L4.80443 8.49908L4.80845 8.50315L0.354575 12.9572C0.125927 13.1859 0 13.4898 0 13.8132C0 14.1366 0.125927 14.4408 0.354575 14.6695L1.59619 15.9111C1.82465 16.1393 2.12874 16.265 2.45194 16.265C2.77521 16.265 3.07922 16.1393 3.30786 15.9111L7.76113 11.4574L12.8035 16.5L12.8042 16.4993L12.8049 16.5L15.7585 13.5467L10.7151 8.50324L10.7151 8.50319Z" />
          </svg>
        </div>
        <p class="text-foreground mt-4 text-2xl font-bold lg:text-3xl">
          {{ $t("Every event, one place.") }}
        </p>
      </section>

      <section
        class="flex min-h-0 flex-1 items-start justify-center px-4 pb-6 lg:w-[var(--card-col)] lg:flex-none lg:items-center lg:px-0 lg:py-6">
        <div
          class="auth-card bg-surface flex w-full flex-col overflow-y-auto rounded-2xl p-8 shadow-[0_2px_2px_rgba(0,0,0,0.1)] max-h-[calc(100dvh-5.5rem)] lg:max-h-[calc(100dvh-2rem)]"
          :class="wideCard ? 'auth-card--wide max-w-[448px] lg:min-w-[448px]' : 'max-w-[380px] lg:min-w-[380px]'"
          :style="pinnedCardMinHeight ? { minHeight: pinnedCardMinHeight + 'px' } : undefined">
          <slot></slot>
        </div>
      </section>
    </div>

    <p
      class="text-muted-foreground px-4 pb-6 text-center text-xs leading-6 lg:absolute lg:bottom-6 lg:left-10 lg:z-20 lg:px-0 lg:pb-0 lg:text-left">
      © XS2Event B.V. 2020–{{ year }}<template v-if="version"> | Version: {{ version }}</template>
    </p>
  </div>
</template>

<script setup lang="ts">
defineProps<{
  title?: string;
  path?: string;
}>();

import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { LanguageMenu } from "@xs2event/js-lib/components";
import { LanguageIcon } from "@xs2event/js-lib/icons";
import { config } from "@/config";
import ContactForm from "@/components/ContactForm.vue";
import { useOnboardingHero } from "@/composables/useOnboardingHero";
import { useActivationHero } from "@/composables/useActivationHero";
import { useRecoveryHero } from "@/composables/useRecoveryHero";
import { useSignInHero } from "@/composables/useSignInHero";
import signInHero1 from "@/assets/images/sign-in/sign-in-hero-1.jpg";
import signInHero2 from "@/assets/images/sign-in/sign-in-hero-2.jpg";
import signInHero3 from "@/assets/images/sign-in/sign-in-hero-3.jpg";
import signInHero4 from "@/assets/images/sign-in/sign-in-hero-4.jpg";
import signInHero5 from "@/assets/images/sign-in/sign-in-hero-5.jpg";
import signInHero6 from "@/assets/images/sign-in/sign-in-hero-6.jpg";
import signInHero7 from "@/assets/images/sign-in/sign-in-hero-7.jpg";
import signInHero8 from "@/assets/images/sign-in/sign-in-hero-8.jpg";
import signInHero9 from "@/assets/images/sign-in/sign-in-hero-9.jpg";
import signInHero10 from "@/assets/images/sign-in/sign-in-hero-10.jpg";
import onboardingHero1 from "@/assets/images/sign-in/onboarding-hero-1.jpg";
import onboardingHero2 from "@/assets/images/sign-in/onboarding-hero-2.jpg";
import onboardingHero3 from "@/assets/images/sign-in/onboarding-hero-3.jpg";
import onboardingHeroSubmitted from "@/assets/images/sign-in/onboarding-hero-submitted.jpg";
import activationHero from "@/assets/images/sign-in/activation-hero.jpg";
import activationHeroCreated from "@/assets/images/sign-in/activation-hero-created.jpg";
import recoveryHero from "@/assets/images/sign-in/recovery-hero.jpg";
import recoveryHeroSent from "@/assets/images/sign-in/recovery-hero-sent.jpg";
import heroEdgeMask from "@/assets/images/sign-in/hero-edge-mask.svg";

const route = useRoute();
const year = new Date().getFullYear();
const version = config.VERSION;
const wideCard = computed(() => Boolean(route.meta.wideCard));
const isOnboarding = computed(() => route.meta.hero === "onboarding");
const isActivation = computed(() => route.meta.hero === "activation");
const isRecovery = computed(() => route.meta.hero === "recovery");
const skipHeroOverlay = computed(() => isOnboarding.value || isActivation.value || isRecovery.value);

// Measured px height of each flow's tallest step/state at the card's fixed width, so switching steps/states doesn't resize the card.
const RECOVERY_CARD_MIN_HEIGHT = 364; // "Recovery email sent" state
const ACTIVATION_CARD_MIN_HEIGHT = 398; // "Choose your password" form state
const pinnedCardMinHeight = computed(() => {
  if (isRecovery.value) return RECOVERY_CARD_MIN_HEIGHT;
  if (isActivation.value) return ACTIVATION_CARD_MIN_HEIGHT;
  return null;
});
const { onboardingHeroKey } = useOnboardingHero();
const { activationHeroKey } = useActivationHero();
const { recoveryHeroKey } = useRecoveryHero();
const { signInHeroIndex } = useSignInHero();
const openContactForm = ref(false);

const onboardingHeroes = [onboardingHero1, onboardingHero2, onboardingHero3] as const;
const signInHeroes = [
  signInHero1,
  signInHero2,
  signInHero3,
  signInHero4,
  signInHero5,
  signInHero6,
  signInHero7,
  signInHero8,
  signInHero9,
  signInHero10,
] as const;

const resolvedHero = computed(() => {
  if (isRecovery.value) {
    return recoveryHeroKey.value === "sent" ? recoveryHeroSent : recoveryHero;
  }
  if (isActivation.value) {
    return activationHeroKey.value === "created" ? activationHeroCreated : activationHero;
  }
  if (!isOnboarding.value) return signInHeroes[signInHeroIndex.value] ?? signInHeroes[0];
  if (onboardingHeroKey.value === "submitted") return onboardingHeroSubmitted;
  return onboardingHeroes[onboardingHeroKey.value] ?? onboardingHero1;
});

onMounted(() => {
  const preload: string[] = [];
  if (isRecovery.value) preload.push(recoveryHero, recoveryHeroSent);
  else if (isActivation.value) preload.push(activationHero, activationHeroCreated);
  else if (isOnboarding.value) preload.push(...onboardingHeroes, onboardingHeroSubmitted);
  for (const src of preload) {
    const img = new Image();
    img.src = src;
  }
});
</script>

<style scoped>
/* Desktop Figma 1920 frame as percentages so any lg+ width keeps the same composition. */
.sign-in-shell {
  --hero-width: 36.6%;
  --copy-width: 56.5%;
  --copy-pad: 6.72%;
  --card-col: 23.3%;
}
</style>

<!-- Select options teleport to body — must be unscoped. -->
<style>
li.bg-primary > span.absolute {
  color: var(--color-foreground);
}
</style>
